import React, { useEffect, useRef, useState } from 'react';
import api from '../utils/api';
import { useCartStore } from '../store/cartStore';

/**
 * Shared BXGY (Buy X Get Y) offer helpers for the cart panel and checkout (Oct 2026).
 *
 * useBxgyOffers({ primary })
 *  - fetches the offers the current cart unlocks (GET /cart/bxgy-offers) whenever items/coupons change;
 *  - primary instance only (the cart panel, always mounted on the POS page):
 *      • AUTO-APPLIES an offer whose "buy" items are met and whose "get" (Y) item is already in the cart
 *        (normal apply rules; a code the staff removed by hand is not re-applied until the cart is cleared);
 *      • raises a WARNING pop-up when an applied offer rewards a Y item that saves nothing (offer price ≥
 *        the item's price) — information only, the sale continues.
 *  Non-primary instances (checkout) just read the same offers to show the "offer unlocked" cards.
 */
export function useBxgyOffers({ primary = false } = {}) {
  const cart = useCartStore(s => s.cart);
  const applyDiscount = useCartStore(s => s.applyDiscount);
  const dismissed = useCartStore(s => s.bxgyDismissed);
  const resetDismissed = useCartStore(s => s.resetBxgyDismissed);
  const [offers, setOffers] = useState([]);
  const [autoErrors, setAutoErrors] = useState({});
  const [warning, setWarning] = useState(null); // { code, text }
  const attempted = useRef(new Set());
  const warned = useRef(new Set());
  const items = cart.items || [];
  const cartKey = items.map(i => `${i.variantId}@${i.locationId}x${i.quantity}`).join(',') + '|' + (cart.discounts || []).map(d => d.code).join(',');

  useEffect(() => {
    if (!items.length) {
      setOffers([]);
      if (primary && dismissed.length) resetDismissed(); // empty cart = new customer
      return;
    }
    let alive = true;
    const t = setTimeout(() => {
      api.get('/cart/bxgy-offers', { params: { locationId: localStorage.getItem('selectedLocation') } })
        .then(r => { if (alive) setOffers(r.data.offers || []); })
        .catch(() => { if (alive) setOffers([]); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  }, [cartKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-apply offers whose X is met and whose Y is already in the cart.
  useEffect(() => {
    if (!primary) return;
    const todo = offers.filter(o => o.autoApply && !o.applied && !dismissed.includes(o.code) && !attempted.current.has(`${o.code}|${cartKey}`));
    if (!todo.length) return;
    let cancelled = false;
    (async () => {
      for (const o of todo) {
        if (cancelled) return;
        attempted.current.add(`${o.code}|${cartKey}`);
        const r = await applyDiscount(o.code, localStorage.getItem('selectedLocation'), { autoAddY: false });
        setAutoErrors(e => { const n = { ...e }; if (r.success) delete n[o.code]; else n[o.code] = r.error || 'Could not apply'; return n; });
        if (r.success) return; // the cart changed — offers are refetched and re-checked
      }
    })();
    return () => { cancelled = true; };
  }, [offers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Warn once per (offer, situation) when a rewarded Y item saves nothing.
  useEffect(() => {
    if (!primary) return;
    const d = (cart.discounts || []).find(x => x.level === 'bxgy' && x.bxgyWarning && !warned.current.has(`${x.code}|${x.bxgyWarning}`));
    if (d) { warned.current.add(`${d.code}|${d.bxgyWarning}`); setWarning({ code: d.code, text: d.bxgyWarning }); }
  }, [cart.discounts, primary]);

  return { offers, autoErrors, dismissed, warning, setWarning };
}

export const addYLabel = (o) => (o.yLabels && o.yLabels.length === 1) ? `Add ${o.yLabels[0]}` : 'Add offer item';

// "Offer unlocked" cards for offers that are NOT applied (yet). onAdd(offer) opens the Y picker,
// onApply(offer) applies the code by hand (after an auto-apply failure, or a code the staff removed).
export function BxgyUnlockedCards({ offers, autoErrors = {}, dismissed = [], onAdd, onApply, compact = false, disabled = false }) {
  const list = (offers || []).filter(o => !o.applied);
  if (!list.length) return null;
  const btn = (bg, color = '#fff') => ({ padding: compact ? '4px 9px' : '5px 10px', background: disabled ? '#d1d5db' : bg, color, border: 'none', borderRadius: '8px', fontSize: compact ? '11px' : '12px', fontWeight: 700, cursor: disabled ? 'not-allowed' : 'pointer' });
  return (
    <>
      {list.map(o => {
        const err = autoErrors[o.code];
        const wasRemoved = dismissed.includes(o.code);
        return (
          <div key={`bx-${o.code}`} style={{ marginBottom: '10px', padding: compact ? '8px 10px' : '10px 12px', background: '#fffbeb', border: '1px dashed #f59e0b', borderRadius: '10px' }}>
            <div style={{ fontSize: compact ? '12px' : '13px', fontWeight: 700, color: '#92400e' }}>🎁 {o.code} — offer unlocked</div>
            <div style={{ fontSize: '11px', color: '#78716c', marginTop: '2px' }}>
              {o.hint || 'The offer items are in the cart.'}
              {o.autoApply && !wasRemoved && !err ? ' Applying the offer…' : ''}
              {(!o.autoApply && o.needQty > 0) ? ' The code is applied automatically when you add it.' : ''}
            </div>
            {o.warning && <div style={{ fontSize: '11px', color: '#b45309', marginTop: '3px' }}>⚠️ {o.warning}</div>}
            {err && <div style={{ fontSize: '11px', color: '#b42318', marginTop: '3px' }}>Couldn't apply automatically: {err}</div>}
            {wasRemoved && <div style={{ fontSize: '11px', color: '#78716c', marginTop: '3px' }}>Removed by staff — not applied automatically.</div>}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
              {o.needQty > 0 && <button disabled={disabled} onClick={() => onAdd && onAdd(o)} style={btn('#f59e0b')}>＋ {addYLabel(o)}</button>}
              {o.autoApply && (wasRemoved || err) && onApply && <button disabled={disabled} onClick={() => onApply(o)} style={{ ...btn('#fff', '#92400e'), border: '1px solid #f59e0b' }}>Apply {o.code}</button>}
            </div>
          </div>
        );
      })}
    </>
  );
}

// Non-blocking warning: the offer's fixed price is not lower than a rewarded item's price.
export function BxgyWarningPopup({ warning, onClose }) {
  if (!warning) return null;
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 3200, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '14px', padding: '20px 22px', width: '400px', maxWidth: '90vw', boxShadow: '0 20px 50px rgba(0,0,0,0.25)', borderTop: '4px solid #f59e0b' }}>
        <div style={{ fontSize: '15px', fontWeight: 800, color: '#92400e', marginBottom: '6px' }}>⚠️ {warning.code} — no saving on an offer item</div>
        <div style={{ fontSize: '13px', color: '#374151', lineHeight: 1.5 }}>{warning.text}</div>
        <div style={{ fontSize: '11.5px', color: '#9ca3af', marginTop: '8px' }}>This is only a warning — you can continue with the sale.</div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>OK, continue</button>
        </div>
      </div>
    </div>
  );
}
